import React, { forwardRef, useRef } from 'react';

import { ContentSizeSwitcher } from 'src/application/components/shared/ContentSizeSwitcher';
import { useSwitch } from 'src/application/hooks/useSwitch';
import { FroalaEditorsSettings } from 'src/config/configs';

import { StudentAudioAssetPlayer } from '../../../components/Audio/editorComponents/StudentAudioAssetPlayer';
import { ContentWrapper } from '../../../components/ContentWrapper/ContentWrapper';
import { ModalType } from '../../../components/ContentWrapper/Modal/Modal';
import { TabsBar } from '../../../components/TabsBar/TabsBar';
import { ContentBlocksRenderer } from '../../../ContentZone/student/ContentBlocksRenderer';
import { useContentRequirementsContext } from '../../RestrictNavigation/ContentRequirementsProvider';
import { Navigation } from '../components/Navigation';
import { HotspotTab, ModalSize } from '@ael/core-api';
import * as Immutable from 'immutable';

interface DesktopNonSequencedFlowProps {
  tabs: Immutable.List<HotspotTab>;
  selectedTab: HotspotTab;
  goToNext?: () => void;
  goToPrevious: () => void;
  selectedTabIndex: number;
  onTabSelected: (tabIndex: number) => void;

  onDismiss: () => void;
  modalSize: ModalSize;
  froalaEditorsSettings: FroalaEditorsSettings;
}

const MAX_TABS_VISIBLE = 3;
const MAX_TABS_VISIBLE_SMALL_WINDOW = 1;

export const DesktopNonSequencedFlow = forwardRef(
  (
    {
      tabs,
      goToNext,
      goToPrevious,
      selectedTabIndex,
      selectedTab,
      onTabSelected,
      onDismiss,
      modalSize,
      froalaEditorsSettings,
    }: DesktopNonSequencedFlowProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const requirements = useContentRequirementsContext();

    const isFullscreenModalSize = modalSize === ModalSize.Fullscreen;
    const maxTabsVisible = modalSize === ModalSize.Fullscreen ? MAX_TABS_VISIBLE : MAX_TABS_VISIBLE_SMALL_WINDOW;
    const allTabsVisible = tabs.size <= maxTabsVisible;
    const toolbarRef = useRef(null);

    const panelVisibilitySwitch = useSwitch(false);

    return (
      <ContentWrapper alwaysShowControl={!isFullscreenModalSize}>
        <ContentSizeSwitcher isSmallModal={modalSize === ModalSize.Sidebar} panelVisibility={panelVisibilitySwitch}>
          <ContentWrapper.Modal
            ref={ref}
            modalType={modalSize === ModalSize.Small ? ModalType.small : ModalType.default}
          >
            <ContentWrapper.TopBar full={!allTabsVisible}>
              <TabsBar
                tabs={tabs}
                maxVisibleTabs={maxTabsVisible}
                selectedTabIndex={selectedTabIndex}
                onTabSelected={onTabSelected}
                hideShowAll={!isFullscreenModalSize}
              />
            </ContentWrapper.TopBar>
            <ContentWrapper.DesktopContent key={selectedTab.id}>
              <ContentWrapper.Title title={selectedTab.title} />
              <ContentWrapper.Body noHorizontalPadding>
                <ContentBlocksRenderer
                  content={selectedTab.content}
                  contentItem={selectedTab}
                  inSmallWindow={!isFullscreenModalSize}
                  froalaEditorsSettings={froalaEditorsSettings}
                />
              </ContentWrapper.Body>
            </ContentWrapper.DesktopContent>
            <ContentWrapper.Toolbar ref={toolbarRef}>
              <Navigation
                ref={toolbarRef}
                goToNext={selectedTabIndex < tabs.size - 1 ? goToNext : undefined}
                onCancel={onDismiss}
                goToPrevious={selectedTabIndex > 0 ? goToPrevious : undefined}
                progressDisabled={!requirements.fulfilled}
                audioPlayer={
                  <StudentAudioAssetPlayer
                    uniqueAudioKey={selectedTab.id}
                    audioAsset={selectedTab.audioAsset}
                    isMobileDevice={false}
                  />
                }
                isSmallWindow={!isFullscreenModalSize}
              />
            </ContentWrapper.Toolbar>
            <ContentWrapper.Caption />
          </ContentWrapper.Modal>
        </ContentSizeSwitcher>
      </ContentWrapper>
    );
  }
);
